/**
 * `HoldAction` (CDMX-18412) -- the registered `React.FC<SlotProps>` container for the Hold action:
 * renders the icon-only trigger button, owns everything `HoldModal` (presentational, `../HoldModal`)
 * doesn't -- reading `api` from `useCodingScreen()`, deriving the "Logged as" identity from the
 * bound slot `value`, the open/close + submission STATE MACHINE (`idle` -> `placing` ->
 * (`loadingNext`) -> `error` | `loadNextError`, or back to closed on success), and the trigger ref
 * `HoldModal` restores focus to via `finalFocusRef`. `HoldModal` owns FORM state only
 * (reasons/note/reminder/validation) and self-resets on every open -- this component never reaches
 * into that.
 *
 * `loadNextError` is a DISTINCT state from the generic `placeHold`-failure `error`: it fires only
 * when `placeHold` already SUCCEEDED and the subsequent `loadNextCase` then rejected, i.e. the hold
 * is genuinely saved but navigation failed. `HoldModal` freezes its form and shows "Stay on this
 * case" / "Retry loading next" for this state instead of the normal controls -- see that
 * component's own doc comment.
 *
 * Registered into `registry.tsx`'s `DEFAULT_SLOT_REGISTRY` and seeded into the CODING
 * `DEFAULT_CODING_SCREEN_CONFIG.header.patientSummary.fields` (order 1, `presentation:'plain'`,
 * `contentId:'session.currentUser'`, CDMX-18412) so the production shell renders it; grading omits it
 * (holds are a coder action). This file's own test still renders `HoldAction` directly.
 */
import { Box, Button, Tooltip } from '@chakra-ui/react';
import React, { useRef, useState } from 'react';

import { useCodingScreen } from '../../interface/context';
import type { HoldPayload, SessionUser } from '../../interface/hold';
import type { SlotProps } from '../../interface/slotProps';
import { HoldModal, type HoldSubmitState } from '../HoldModal';
import { HOLD_COLORS, CASE_ACTION_RADIUS } from '../../styles/tokens';

/** The action's own accessible name. `SlotProps.item` (typed as the base `Slot` --
 *  `interface/slotProps.ts`) carries no `label` field; only a host container (e.g.
 *  `PatientSummaryBar`) reads `field.label`, as its own fallback -- it's never forwarded into a
 *  resolved component (same pattern as `PatientInfo`, the other real registered component). So the
 *  Hold trigger's accessible name is a fixed string here, not a reach into `item`. */
const HOLD_LABEL = 'Hold';

/** Narrow the slot `value` (unknown) to a SessionUser: only a genuine non-null non-array object;
 *  everything else (undefined/null/array/primitive) → {} so the name falls back, never throws. */
const asSessionUser = (value: unknown): SessionUser =>
  value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as SessionUser)
    : {};
const nameOf = (u: SessionUser): string =>
  typeof u.displayName === 'string' && u.displayName.trim().length > 0
    ? u.displayName
    : 'XXX'; // placeholder until a real current user is injected (design: "Logged as XXX | DATE")

export const HoldAction: React.FC<SlotProps> = ({ value }) => {
  const { api } = useCodingScreen();
  const [isOpen, setIsOpen] = useState(false);
  const [loggedAs, setLoggedAs] = useState<{ name: string; at: Date } | null>(
    null
  );
  const [submitState, setSubmitState] = useState<HoldSubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  // True once `placeHold` has SUCCEEDED for the current open modal session. Guards the "load-next
  // failed, user retries" path from placing the same hold twice: a `loadNextCase` rejection sets
  // `submitState:'loadNextError'` -- HoldModal freezes its form for that state and offers only
  // "Stay on this case" / "Retry loading next" -- so a retry click must skip the already-succeeded
  // `placeHold` and only retry the navigation.
  const placedRef = useRef(false);
  // Bumped on every `open()` and used as `<HoldModal key={openGen}>`'s key -- remounts HoldModal
  // fresh each open (no brief stale render between closing and the reset effect committing); the
  // effect-based reset inside HoldModal itself stays as defense-in-depth.
  const [openGen, setOpenGen] = useState(0);

  const open = (): void => {
    setLoggedAs({ name: nameOf(asSessionUser(value)), at: new Date() }); // frozen snapshot per open
    setSubmitState('idle');
    setErrorMessage(undefined);
    placedRef.current = false; // fresh hold per open
    setOpenGen((g) => g + 1);
    setIsOpen(true);
  };
  const close = (): void => {
    setIsOpen(false);
    setSubmitState('idle');
    setErrorMessage(undefined);
  };

  const onSubmit = async (
    payload: HoldPayload,
    opts: { loadNext: boolean }
  ): Promise<void> => {
    setErrorMessage(undefined);
    // No adapter wired yet -- `api` stays undefined until a dashboard-ui adapter injects a real
    // CodingScreenApi (PT-1). The shell IS live-mounted (CMx.tsx routes), so do NOT silently no-op
    // placeHold and close() as if the hold succeeded; surface an explicit "unavailable" error and
    // keep the modal open. (Guards `api` non-null for the calls below.)
    if (!api?.placeHold) {
      setSubmitState('error');
      setErrorMessage('Holds are not available yet.');
      return;
    }
    if (!placedRef.current) {
      try {
        setSubmitState('placing');
        await api.placeHold(payload);
        placedRef.current = true; // never call placeHold again this session
      } catch {
        setSubmitState('error');
        setErrorMessage("Couldn't place the hold. Try again.");
        return; // nothing was placed — a retry correctly re-attempts placeHold
      }
    }
    if (opts.loadNext) {
      try {
        setSubmitState('loadingNext');
        await api.loadNextCase();
      } catch {
        // Distinct from the placeHold-failure `'error'` path above -- the hold IS already saved
        // (placedRef.current is true), so a generic 'error' here would re-enable the whole form as
        // if nothing had happened, even though a retry can never re-save and edits made now could
        // never be persisted. `loadNextError` lets HoldModal freeze the form and offer only "Stay
        // on this case" / "Retry loading next" instead.
        setSubmitState('loadNextError');
        setErrorMessage("Hold saved, but couldn't load the next case.");
        return; // stay open; hold already placed — do not re-place
      }
    }
    close();
  };

  return (
    <>
      <Tooltip label={HOLD_LABEL} placement="bottom" openDelay={300}>
        <Button
          ref={triggerRef}
          data-testid="hold-icon"
          aria-label={HOLD_LABEL}
          onClick={open}
          variant="unstyled"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          minW="auto"
          h="auto"
          p={0}
        >
          <Box
            aria-hidden
            bg={HOLD_COLORS.iconBg}
            borderRadius={CASE_ACTION_RADIUS.control}
            w="28px"
            h="28px"
            flexShrink={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="3px"
          >
            <Box w="3px" h="12px" bg={HOLD_COLORS.iconBar} borderRadius="1px" />
            <Box w="3px" h="12px" bg={HOLD_COLORS.iconBar} borderRadius="1px" />
          </Box>
        </Button>
      </Tooltip>
      {loggedAs && (
        <HoldModal
          key={openGen}
          isOpen={isOpen}
          onClose={close}
          loggedAs={loggedAs}
          onSubmit={onSubmit}
          submitState={submitState}
          errorMessage={errorMessage}
          finalFocusRef={triggerRef}
        />
      )}
    </>
  );
};
